import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error, timeout
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/stripe/checkout-status/${sid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        toast.success('Payment successful!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Status check error:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const handleContinue = () => {
    // Navigate based on what was purchased (could be enhanced with metadata)
    navigate('/dashboard/modules');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)'
    }}>
      <Card className="max-w-md w-full glass-effect border-cyan-500/30">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-cyan-400 animate-spin mb-4" />
              <CardTitle className="text-2xl text-white">
                Confirming Payment...
              </CardTitle>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-green-400 mb-4" />
              <CardTitle className="text-2xl text-white">
                Payment Successful!
              </CardTitle>
            </>
          )}
          
          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <CardTitle className="text-2xl text-white">
                Payment Issue
              </CardTitle>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <AlertCircle className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
              <CardTitle className="text-2xl text-white">
                Verification Pending
              </CardTitle>
            </>
          )}
        </CardHeader>
        
        <CardContent className="space-y-4 text-center">
          {status === 'checking' && (
            <p className="text-gray-300">
              Please wait while we confirm your payment...
            </p>
          )}
          
          {status === 'success' && (
            <>
              <p className="text-gray-300">
                Thank you for your purchase! Your access has been activated.
              </p>
              {paymentDetails && (
                <div className="p-4 bg-green-900/20 rounded-lg space-y-2">
                  {/* Subtotal */}
                  {paymentDetails.amount_subtotal > 0 && (
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Subtotal:</span>
                      <span>${(paymentDetails.amount_subtotal / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Tax */}
                  {paymentDetails.tax_amount > 0 && (
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Tax:</span>
                      <span>${(paymentDetails.tax_amount / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Total */}
                  <div className="flex justify-between text-green-400 font-semibold pt-2 border-t border-green-800">
                    <span>Total Paid:</span>
                    <span>${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}</span>
                  </div>
                </div>
              )}
              <Button onClick={handleContinue} className="w-full btn-primary">
                Continue to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <p className="text-gray-300">
                There was an issue confirming your payment. If you were charged, please contact support.
              </p>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Return to Dashboard
              </Button>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <p className="text-gray-300">
                Payment verification is taking longer than expected. Please check your email for confirmation, or contact support if you have concerns.
              </p>
              <Button onClick={handleContinue} className="w-full btn-primary">
                Continue to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
